import React, { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiClient } from '@/utils/api'
import { AlertCircle, CheckCircle2, Clock, User, Calendar, Edit, Trash2, Filter, ChevronDown, X, Library, ClipboardList } from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import toast from 'react-hot-toast'
import { getTodayDateString, extractDateTimeInTimezone, extractDateInTimezoneSync, parseDueDate } from '@/utils/timezoneUtils'
import {
  getAssignedUserId,
  filterAssignments,
  getDisplayStatus,
  needsVerification as needsVerificationFn,
  ASSIGNMENT_STATUS,
} from '@/utils/assignmentUtils'
import {
  useAssignments,
  useVerifyAssignment,
  useParentCompleteAssignment,
  useUnverifyAssignment,
  useUpdateAssignment,
  useDeleteAssignment,
  Assignment as AssignmentType
} from '@/hooks/useAssignments'
import { ChoreStatusBadge } from '@/components/ChoreStatusStepper'
import { sortedChoreSteps } from '@/utils/choreSteps'
import { useConfetti } from '@/components/ConfettiCelebration'

interface Assignment {
  id: number | string  // Can be number (old format) or UUID string (new format)
  user_id?: number  // Legacy field
  assigned_to?: string  // New field - UUID of assigned user
  username?: string
  display_name?: string
  chore_id: number | string
  chore_name?: string
  chore?: {
    name: string
    category: string
  }
  assigned_user?: {
    id?: string
    display_name: string
    username: string
  }
  status: string
  assigned_date?: string
  due_date: string
  completed_at?: string
  verified_at?: string
  points?: number
  points_offered?: number
  points_earned?: number
  photo_url?: string
}

// NOTE: Date normalization now uses extractDateInTimezoneSync from timezoneUtils.ts
// This properly converts UTC dates from the backend to the family's timezone

// Helper to format date with day of week
const formatDueDate = (dateStr: string): string => {
  const d = new Date(dateStr.includes?.('T') ? dateStr : dateStr + 'T12:00:00')
  if (isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  })
}

export const ChoresManagementView: React.FC = () => {
  // Read query params for initial filter selection
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSection = searchParams.get('section') as 'all' | 'verification' | 'overdue' | 'active' | 'history' | null

  // Use React Query for assignments - automatic cache invalidation on mutations
  const { data: assignments = [], isLoading: loading, refetch } = useAssignments({ dateFilter: 'all' })

  // Confetti celebration hook
  const { showConfetti, celebrate, onConfettiComplete, ConfettiComponent } = useConfetti()

  // Mutations with automatic cache invalidation
  const verifyMutation = useVerifyAssignment()
  const parentCompleteMutation = useParentCompleteAssignment()
  const unverifyMutation = useUnverifyAssignment()
  const updateMutation = useUpdateAssignment()
  const deleteMutation = useDeleteAssignment()

  // Set initial filter from query param or default to 'active'
  const [filter, setFilter] = useState<'all' | 'verification' | 'overdue' | 'active' | 'history'>(
    initialSection && ['all', 'verification', 'overdue', 'active', 'history'].includes(initialSection)
      ? initialSection
      : 'active'
  )
  const [selectedKid, setSelectedKid] = useState<string>('all')
  const [selectedChore, setSelectedChore] = useState<string>('all')
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'all'>('all')
  const [showFilters, setShowFilters] = useState(false)
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null)
  const [editDueDate, setEditDueDate] = useState('')
  const [editDueTime, setEditDueTime] = useState('')
  const [editAssignedChild, setEditAssignedChild] = useState<string>('')
  const [editStatus, setEditStatus] = useState('')
  const [editPoints, setEditPoints] = useState<number>(0)
  const [editOpenMode, setEditOpenMode] = useState<string>('')
  const [todayDateString, setTodayDateString] = useState<string>('')
  const [children, setChildren] = useState<{id: string | number, display_name: string}[]>([])
  const [verificationEnabled, setVerificationEnabled] = useState<boolean>(true)

  // Confirmation modal state
  const [confirmingAction, setConfirmingAction] = useState<{assignment: Assignment, action: 'verify' | 'complete'} | null>(null)
  const [actionNotes, setActionNotes] = useState('')

  // Verification point options state
  const [verifyPointsMode, setVerifyPointsMode] = useState<'full' | 'partial' | 'bonus' | 'custom'>('full')
  const [customPoints, setCustomPoints] = useState<number>(0)

  useEffect(() => {
    // Initialize timezone and fetch children and feature toggles
    const init = async () => {
      const today = await getTodayDateString()
      setTodayDateString(today)
      fetchChildren()
      fetchFeatureToggles()
    }
    init()
  }, [])

  const fetchFeatureToggles = async () => {
    try {
      const data = await apiClient.get('/api/family/settings')
      const toggles = data.feature_toggles || data.settings?.feature_toggles || {}
      setVerificationEnabled(toggles.chore_verification !== false)
    } catch (error) {
      console.error('Error fetching feature toggles:', error)
      // Default to verification enabled
      setVerificationEnabled(true)
    }
  }

  const fetchChildren = async () => {
    try {
      const data = await apiClient.get('/api/parent/children')
      const childrenList = data.children || data.data || []
      setChildren(childrenList.map((c: any) => ({
        id: c.id,
        display_name: c.display_name || c.username
      })))
    } catch (error) {
      console.error('Error fetching children:', error)
    }
  }


  const handleVerify = (assignmentId: number | string) => {
    const assignment = assignments.find(a => String(a.id) === String(assignmentId))
    if (assignment) {
      setConfirmingAction({ assignment, action: 'verify' })
      setActionNotes('')
      // Reset point options for new verification
      setVerifyPointsMode('full')
      setCustomPoints(assignment.points || assignment.points_offered || 0)
    }
  }

  // Calculate points based on selected mode
  const getPointsToAward = () => {
    if (!confirmingAction) return 0
    const basePoints = confirmingAction.assignment.points || confirmingAction.assignment.points_offered || 0
    switch (verifyPointsMode) {
      case 'full': return basePoints
      case 'partial': return Math.floor(basePoints / 2)
      case 'bonus': return Math.floor(basePoints * 1.5)
      case 'custom': return customPoints
      default: return basePoints
    }
  }

  const handleComplete = (assignmentId: number | string) => {
    const assignment = assignments.find(a => String(a.id) === String(assignmentId))
    if (assignment) {
      setConfirmingAction({ assignment, action: 'complete' })
      setActionNotes('')
      // Initialize point options for complete action
      setVerifyPointsMode('full')
      setCustomPoints(assignment.points || assignment.points_offered || 0)
    }
  }

  const handleConfirmAction = async () => {
    if (!confirmingAction) return

    const { assignment, action } = confirmingAction
    const childName = assignment.assigned_user?.display_name || assignment.display_name
    const pointsToAward = getPointsToAward()

    try {
      if (action === 'verify') {
        // Verify action awards points based on selected mode
        await verifyMutation.mutateAsync({
          assignmentId: String(assignment.id),
          approved: true,
          verificationNotes: actionNotes || undefined,
          pointsAwarded: pointsToAward
        })
        toast.success(`Assignment verified! ${pointsToAward} points awarded to ${childName}!`)
        celebrate() // Trigger confetti celebration
      } else if (action === 'complete') {
        await parentCompleteMutation.mutateAsync({
          assignmentId: String(assignment.id),
          notes: actionNotes || undefined,
          pointsAwarded: verificationEnabled ? undefined : pointsToAward
        })
        if (verificationEnabled) {
          toast.success(`Chore marked as done for ${childName}. Verify to award points.`)
        } else {
          toast.success(`Assignment completed! ${pointsToAward} points awarded to ${childName}!`)
          celebrate()
        }
      }

      setConfirmingAction(null)
      setActionNotes('')
      // No need to call fetchAssignments - cache automatically invalidated by mutations
    } catch (error) {
      console.error(`Error ${action}ing assignment:`, error)
      toast.error(`Failed to ${action} assignment`)
    }
  }

  const handleCancelAction = () => {
    setConfirmingAction(null)
    setActionNotes('')
  }

  const handleUnverify = async (assignmentId: number | string) => {
    try {
      await unverifyMutation.mutateAsync(String(assignmentId))
      toast.success('Verification removed')
      // No need to call fetchAssignments - cache automatically invalidated
    } catch (error) {
      console.error('Error unverifying assignment:', error)
      toast.error('Failed to unverify assignment')
    }
  }

  const handleEdit = async (assignmentId: number | string) => {
    const assignment = assignments.find(a => String(a.id) === String(assignmentId))

    if (!assignment) {
      toast.error('Assignment not found')
      return
    }

    // Get the assigned user ID - try multiple fields for compatibility
    const assignedUserId = assignment.assigned_to ||
                          assignment.assigned_user?.id ||
                          (assignment.user_id ? String(assignment.user_id) : '')

    setEditingAssignment(assignment)

    // Convert UTC date to family timezone before extracting date/time
    let extractedDate: string
    let extractedTime: string

    if (assignment.due_date.includes('T')) {
      // ISO datetime string - convert from UTC to family timezone
      const { date, time } = await extractDateTimeInTimezone(assignment.due_date)
      extractedDate = date
      extractedTime = time
    } else {
      // Date-only string - use as-is
      extractedDate = assignment.due_date
      extractedTime = ''
    }

    setEditDueDate(extractedDate)
    setEditDueTime(extractedTime)
    setEditAssignedChild(assignment.status === 'open' || !assignedUserId ? '' : assignedUserId)
    setEditOpenMode(assignment.open_mode || 'claim')
    setEditStatus(assignment.status)
    setEditPoints(assignment.points_offered || assignment.points || 0)
  }

  const handleSaveEdit = async () => {
    if (!editingAssignment) return

    try {
      // Combine date and time for due_date
      // Note: The date/time are in family timezone (from the form inputs)
      // Backend should handle timezone conversion when storing
      let due_date = editDueDate
      if (editDueTime) {
        due_date = `${editDueDate}T${editDueTime}:00`
      }

      await updateMutation.mutateAsync({
        assignmentId: String(editingAssignment.id),
        updates: {
          due_date,
          assigned_to: editAssignedChild || null,
          status: editAssignedChild ? editStatus : 'open',
          points_offered: editPoints,
          open_mode: editAssignedChild ? null : editOpenMode
        }
      })
      toast.success('Assignment updated!')
      setEditingAssignment(null)
      setEditDueDate('')
      setEditDueTime('')
      setEditAssignedChild('')
      setEditStatus('')
      setEditPoints(0)
      // No need to call fetchAssignments - cache automatically invalidated
    } catch (error) {
      console.error('Error updating assignment:', error)
      toast.error('Failed to update assignment')
    }
  }

  const handleCancelEdit = () => {
    setEditingAssignment(null)
    setEditDueDate('')
    setEditDueTime('')
    setEditAssignedChild('')
    setEditStatus('')
    setEditPoints(0)
  }

  const handleDelete = async (assignmentId: number | string) => {
    if (!confirm('Are you sure you want to delete this assignment?')) {
      return
    }

    try {
      await deleteMutation.mutateAsync(String(assignmentId))
      toast.success('Assignment deleted')
      // No need to call fetchAssignments - cache automatically invalidated
    } catch (error) {
      console.error('Error deleting assignment:', error)
      toast.error('Failed to delete assignment')
    }
  }

  const needsVerification = assignments.filter(needsVerificationFn)

  // "Done" for these predicates means kid marked complete OR parent verified.
  // Historically we only checked status, which missed rows where completed_at
  // was set but status hadn't caught up (drift). getDisplayStatus captures
  // both cases.
  const isDoneOrVerified = (a: Assignment) => {
    const d = getDisplayStatus(a)
    return d === ASSIGNMENT_STATUS.COMPLETED || d === ASSIGNMENT_STATUS.VERIFIED
  }

  // Use family timezone for overdue detection - normalize dates for comparison
  const overdue = assignments.filter(a => {
    if (isDoneOrVerified(a) || !todayDateString) return false
    // Normalize the due_date to YYYY-MM-DD format for comparison
    const dueDateStr = extractDateInTimezoneSync(a.due_date)
    // Compare date strings: if due date is less than today, it's overdue
    return dueDateStr < todayDateString
  })

  const active = assignments.filter(a => {
    // Exclude completed and verified assignments (drift-safe)
    if (isDoneOrVerified(a)) return false
    // Exclude overdue assignments (they're in the overdue category)
    if (todayDateString) {
      const dueDateStr = extractDateInTimezoneSync(a.due_date)
      if (dueDateStr < todayDateString) return false
    }
    // Include pending, assigned, in_progress, and open (bidding)
    return a.status === 'pending' || a.status === 'assigned' || a.status === 'in_progress' || a.status === 'open'
  })

  const history = assignments.filter(a => getDisplayStatus(a) === ASSIGNMENT_STATUS.VERIFIED)

  // Get unique chores for filter
  const uniqueChores = useMemo(() => {
    const chores = new Map<string, string>()
    assignments.forEach(a => {
      const id = String(a.chore_id)
      const name = a.chore?.name || 'Unknown'
      if (!chores.has(id)) {
        chores.set(id, name)
      }
    })
    return Array.from(chores.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [assignments])

  const getFilteredAssignments = () => {
    let result: typeof assignments
    switch (filter) {
      case 'verification':
        result = needsVerification
        break
      case 'overdue':
        result = overdue
        break
      case 'active':
        result = active
        break
      case 'history':
        result = history.sort((a, b) => {
          const dateA = new Date(a.verified_at || a.completed_at || a.due_date || a.created_at).getTime()
          const dateB = new Date(b.verified_at || b.completed_at || b.due_date || b.created_at).getTime()
          return dateB - dateA
        })
        break
      default:
        result = [...assignments].sort((a, b) => {
          const dateA = new Date(a.due_date || a.created_at).getTime()
          const dateB = new Date(b.due_date || b.created_at).getTime()
          return dateB - dateA
        })
    }

    // Apply kid filter
    if (selectedKid !== 'all') {
      result = result.filter(a => {
        const assignedId = a.assigned_to || a.assigned_user?.id || String(a.user_id)
        return assignedId === selectedKid
      })
    }

    // Apply chore filter
    if (selectedChore !== 'all') {
      result = result.filter(a => String(a.chore_id) === selectedChore)
    }

    // Apply date range filter
    if (dateRange !== 'all' && todayDateString) {
      const today = new Date(todayDateString + 'T12:00:00')
      result = result.filter(a => {
        const dueDate = parseDueDate(a.due_date)
        switch (dateRange) {
          case 'today':
            return dueDate.toDateString() === today.toDateString()
          case 'week': {
            const weekFromNow = new Date(today)
            weekFromNow.setDate(weekFromNow.getDate() + 7)
            return dueDate >= today && dueDate <= weekFromNow
          }
          case 'month': {
            const monthFromNow = new Date(today)
            monthFromNow.setMonth(monthFromNow.getMonth() + 1)
            return dueDate >= today && dueDate <= monthFromNow
          }
          default:
            return true
        }
      })
    }

    return result
  }

  const filteredAssignments = getFilteredAssignments()
  const activeFiltersCount = (selectedKid !== 'all' ? 1 : 0) + (selectedChore !== 'all' ? 1 : 0) + (dateRange !== 'all' ? 1 : 0)

  const clearFilters = () => {
    setSelectedKid('all')
    setSelectedChore('all')
    setDateRange('all')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading chores...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Chores Management</h2>

        {/* Stats Tiles - Click to filter */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <button
            onClick={() => setFilter('active')}
            className={`rounded-lg p-4 text-left transition-all ${
              filter === 'active'
                ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
                : 'bg-blue-50 hover:bg-blue-100'
            }`}
          >
            <div className={`text-sm mb-1 ${filter === 'active' ? 'text-blue-100' : 'text-gray-600'}`}>Active</div>
            <div className={`text-2xl font-bold ${filter === 'active' ? 'text-white' : 'text-blue-600'}`}>{active.length}</div>
          </button>
          <button
            onClick={() => setFilter('verification')}
            className={`rounded-lg p-4 text-left transition-all ${
              filter === 'verification'
                ? 'bg-orange-600 text-white ring-2 ring-orange-600 ring-offset-2'
                : 'bg-orange-50 hover:bg-orange-100'
            }`}
          >
            <div className={`text-sm mb-1 ${filter === 'verification' ? 'text-orange-100' : 'text-gray-600'}`}>Needs Verification</div>
            <div className={`text-2xl font-bold ${filter === 'verification' ? 'text-white' : 'text-orange-600'}`}>{needsVerification.length}</div>
          </button>
          <button
            onClick={() => setFilter('overdue')}
            className={`rounded-lg p-4 text-left transition-all ${
              filter === 'overdue'
                ? 'bg-red-600 text-white ring-2 ring-red-600 ring-offset-2'
                : 'bg-red-50 hover:bg-red-100'
            }`}
          >
            <div className={`text-sm mb-1 ${filter === 'overdue' ? 'text-red-100' : 'text-gray-600'}`}>Overdue</div>
            <div className={`text-2xl font-bold ${filter === 'overdue' ? 'text-white' : 'text-red-600'}`}>{overdue.length}</div>
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`rounded-lg p-4 text-left transition-all ${
              filter === 'all'
                ? 'bg-gray-700 text-white ring-2 ring-gray-700 ring-offset-2'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            <div className={`text-sm mb-1 ${filter === 'all' ? 'text-gray-300' : 'text-gray-600'}`}>All</div>
            <div className="text-2xl font-bold">{assignments.length}</div>
          </button>
          <button
            onClick={() => setFilter('history')}
            className={`rounded-lg p-4 text-left transition-all ${
              filter === 'history'
                ? 'bg-green-600 text-white ring-2 ring-green-600 ring-offset-2'
                : 'bg-green-50 hover:bg-green-100'
            }`}
          >
            <div className={`text-sm mb-1 ${filter === 'history' ? 'text-green-100' : 'text-gray-600'}`}>History</div>
            <div className={`text-2xl font-bold ${filter === 'history' ? 'text-white' : 'text-green-600'}`}>{history.length}</div>
          </button>
        </div>

        {/* Filter Controls */}
        <div className="flex items-center gap-2 pt-4 border-t">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              showFilters || activeFiltersCount > 0
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {activeFiltersCount > 0 && (
              <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">{activeFiltersCount}</span>
            )}
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>

          {activeFiltersCount > 0 && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-2 py-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
              Clear all
            </button>
          )}

          <div className="flex-1" />

          <span className="text-sm text-gray-500">
            {filteredAssignments.length} assignment{filteredAssignments.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Expandable Filter Panel */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 mt-4 border-t">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kid</label>
              <select
                value={selectedKid}
                onChange={(e) => setSelectedKid(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">All Kids</option>
                {children.map(child => (
                  <option key={child.id} value={child.id}>{child.display_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chore</label>
              <select
                value={selectedChore}
                onChange={(e) => setSelectedChore(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">All Chores</option>
                {uniqueChores.map(chore => (
                  <option key={chore.id} value={chore.id}>{chore.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">Any Time</option>
                <option value="today">Due Today</option>
                <option value="week">Next 7 Days</option>
                <option value="month">Next 30 Days</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Assignments List - Compact View */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {filteredAssignments.length === 0 ? (
          assignments.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="No chores assigned yet"
              description="Start by creating chores in the Chore Library tab, then assign them to your kids. Once assigned, you'll manage them here."
              actionLabel="Go to Chore Library"
              actionHref="/dashboard/chores?tab=library"
            />
          ) : (
            <div className="p-12 text-center">
              <div className="text-gray-400 mb-2">
                <CheckCircle2 className="w-12 h-12 mx-auto" />
              </div>
              <p className="text-gray-600">No {filter !== 'all' ? filter : ''} chores match your filters</p>
            </div>
          )
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredAssignments.map(assignment => {
              const isOverdue = todayDateString && extractDateInTimezoneSync(assignment.due_date) < todayDateString && !isDoneOrVerified(assignment)
              const needsVerify = needsVerificationFn(assignment)
              const isActive = assignment.status === 'assigned' && todayDateString && extractDateInTimezoneSync(assignment.due_date) >= todayDateString
              const isVerified = !!assignment.verified_at

              return (
                <div
                  key={assignment.id}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                    isOverdue ? 'bg-red-50' : needsVerify ? 'bg-orange-50' : ''
                  }`}
                >
                  {/* Avatar */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 ${
                    assignment.status === 'open' ? 'bg-gradient-to-br from-amber-500 to-orange-500' : 'bg-gradient-to-br from-blue-500 to-purple-500'
                  }`}>
                    {assignment.status === 'open' ? '?' : (assignment.assigned_user?.display_name?.charAt(0) || '?')}
                  </div>

                  {/* Main Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 truncate">{assignment.chore?.name || 'Unknown'}</h3>
                      {/* Status Badge with progression stepper */}
                      {assignment.status === 'open' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          Open for bidding
                        </span>
                      ) : (
                        <ChoreStatusBadge
                          status={getDisplayStatus(assignment)}
                          verificationEnabled={verificationEnabled}
                          isParent={true}
                          onAdvance={async (newStatus) => {
                            if (newStatus === 'completed') {
                              handleComplete(assignment.id)
                            } else if (newStatus === 'verified') {
                              handleVerify(assignment.id)
                            }
                          }}
                        />
                      )}
                      {isOverdue && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          <Clock className="w-3 h-3" />
                          Overdue
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {assignment.status === 'open' ? 'Open for bidding' : (assignment.assigned_user?.display_name || 'Unknown')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDueDate(assignment.due_date)}
                      </span>
                      <span className="font-medium text-blue-600">{assignment.points_offered || assignment.points_earned || 0} pts</span>
                    </div>
                  </div>

                  {/* Photo indicator */}
                  {assignment.photo_url && (
                    <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0">
                      <img src={assignment.photo_url} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}

                  {/* Actions - Horizontal */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isVerified && (
                      <button
                        onClick={() => handleUnverify(assignment.id)}
                        className="px-2 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-xs"
                      >
                        Unverify
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(assignment.id)}
                      className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(assignment.id)}
                      className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Edit Assignment Modal */}
      {editingAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4">Edit Assignment</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chore
              </label>
              <div className="text-gray-900 font-medium">{editingAssignment.chore?.name || editingAssignment.chore_name}</div>
              {(() => {
                const editSteps = sortedChoreSteps((editingAssignment.chore as any)?.steps)
                if (editSteps.length === 0) return null
                return (
                  <div className="mt-2 bg-gray-50 rounded p-2">
                    <div className="text-xs font-medium text-gray-500 mb-1">Steps:</div>
                    <ol className="list-decimal ml-4 space-y-0.5 text-xs text-gray-600">
                      {editSteps.map((step, i) => (
                        <li key={i}>{step.text}</li>
                      ))}
                    </ol>
                  </div>
                )
              })()}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assigned To
              </label>
              <select
                value={editAssignedChild}
                onChange={(e) => setEditAssignedChild(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Open Assignment (Anyone)</option>
                {children.map(child => (
                  <option key={child.id} value={child.id}>{child.display_name}</option>
                ))}
              </select>
            </div>

            {editAssignedChild === '' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Open Mode
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditOpenMode('claim')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border ${editOpenMode === 'claim' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300'}`}
                  >
                    First to Claim
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditOpenMode('bid')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border ${editOpenMode === 'bid' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-300'}`}
                  >
                    Bidding
                  </button>
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              {/* Visual Status Stepper */}
              <div className="flex items-center justify-between gap-1">
                {[
                  ...(editAssignedChild === '' ? [{ value: 'open', label: 'Open', color: 'yellow' }] : []),
                  { value: 'pending', label: 'Pending', color: 'gray' },
                  { value: 'assigned', label: 'Assigned', color: 'blue' },
                  { value: 'in_progress', label: 'In Progress', color: 'yellow' },
                  { value: 'completed', label: 'Completed', color: 'orange' },
                  { value: 'verified', label: 'Verified', color: 'green' }
                ].map((step, index, arr) => {
                  const isSelected = editStatus === step.value
                  const statusOrder = ['pending', 'assigned', 'in_progress', 'completed', 'verified']
                  const currentIndex = statusOrder.indexOf(editStatus)
                  const stepIndex = statusOrder.indexOf(step.value)
                  const isPast = stepIndex < currentIndex

                  return (
                    <React.Fragment key={step.value}>
                      <button
                        type="button"
                        onClick={() => setEditStatus(step.value)}
                        className={`flex-1 py-2 px-1 rounded-lg text-xs font-medium transition-all ${
                          isSelected
                            ? step.color === 'gray' ? 'bg-gray-600 text-white'
                            : step.color === 'blue' ? 'bg-blue-600 text-white'
                            : step.color === 'yellow' ? 'bg-yellow-500 text-white'
                            : step.color === 'orange' ? 'bg-orange-500 text-white'
                            : 'bg-green-600 text-white'
                            : isPast
                            ? 'bg-gray-200 text-gray-600'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        <div className="flex flex-col items-center">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center mb-1 ${
                            isSelected
                              ? 'bg-white/30'
                              : isPast
                              ? 'bg-gray-400 text-white'
                              : 'bg-gray-300'
                          }`}>
                            {isPast ? (
                              <CheckCircle2 className="w-3 h-3" />
                            ) : (
                              <span className="text-xs">{index + 1}</span>
                            )}
                          </div>
                          <span className="leading-tight">{step.label}</span>
                        </div>
                      </button>
                      {index < arr.length - 1 && (
                        <div className={`w-2 h-0.5 ${isPast ? 'bg-gray-400' : 'bg-gray-200'}`} />
                      )}
                    </React.Fragment>
                  )
                })}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Points
              </label>
              <input
                type="number"
                value={editPoints}
                onChange={(e) => setEditPoints(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="0"
                placeholder="Points offered"
              />
              <p className="text-xs text-gray-500 mt-1">Points to award when verified</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date
              </label>
              <input
                type="date"
                value={editDueDate}
                onChange={(e) => setEditDueDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Time (optional)
              </label>
              <input
                type="time"
                value={editDueTime}
                onChange={(e) => setEditDueTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty for end of day</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSaveEdit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Save Changes
              </button>
              <button
                onClick={handleCancelEdit}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verify/Complete Confirmation Modal */}
      {confirmingAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">
              {confirmingAction.action === 'verify'
                ? 'Verify Completion'
                : verificationEnabled
                  ? 'Mark as Done'
                  : 'Complete & Award Points'}
            </h3>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Chore:</span>
                <span className="text-sm text-gray-900">{confirmingAction.assignment.chore?.name || confirmingAction.assignment.chore_name}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Child:</span>
                <span className="text-sm text-gray-900">{confirmingAction.assignment.assigned_user?.display_name || confirmingAction.assignment.display_name}</span>
              </div>
            </div>

            {/* Info message when marking done with verification enabled */}
            {confirmingAction.action === 'complete' && verificationEnabled && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
                This will mark the chore as done. Points will be awarded when you verify it.
              </div>
            )}

            {/* Point Options - show for verify, or complete when verification is off */}
            {(confirmingAction.action === 'verify' || (confirmingAction.action === 'complete' && !verificationEnabled)) && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Points to Award
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setVerifyPointsMode('full')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      verifyPointsMode === 'full'
                        ? 'bg-green-100 text-green-800 border-2 border-green-500'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Full ({confirmingAction.assignment.points || confirmingAction.assignment.points_offered || 0} pts)
                  </button>
                  <button
                    type="button"
                    onClick={() => setVerifyPointsMode('partial')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      verifyPointsMode === 'partial'
                        ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-500'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Partial ({Math.floor((confirmingAction.assignment.points || confirmingAction.assignment.points_offered || 0) / 2)} pts)
                  </button>
                  <button
                    type="button"
                    onClick={() => setVerifyPointsMode('bonus')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      verifyPointsMode === 'bonus'
                        ? 'bg-purple-100 text-purple-800 border-2 border-purple-500'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Bonus ({Math.floor((confirmingAction.assignment.points || confirmingAction.assignment.points_offered || 0) * 1.5)} pts)
                  </button>
                  <button
                    type="button"
                    onClick={() => setVerifyPointsMode('custom')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      verifyPointsMode === 'custom'
                        ? 'bg-blue-100 text-blue-800 border-2 border-blue-500'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Custom
                  </button>
                </div>

                {/* Custom points input */}
                {verifyPointsMode === 'custom' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={customPoints}
                      onChange={(e) => setCustomPoints(parseInt(e.target.value) || 0)}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="0"
                      placeholder="Points"
                    />
                    <span className="text-sm text-gray-600">points</span>
                  </div>
                )}

                {/* Display selected points */}
                <div className="mt-3 p-2 bg-green-50 rounded-lg text-center">
                  <span className="text-lg font-bold text-green-600">{getPointsToAward()} points will be awarded</span>
                </div>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (optional)
              </label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="Add any notes about this completion..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleConfirmAction}
                className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity font-medium bg-green-600"
              >
                {confirmingAction.action === 'verify'
                  ? 'Verify & Award Points'
                  : verificationEnabled
                    ? 'Mark as Done'
                    : 'Complete & Award Points'}
              </button>
              <button
                onClick={handleCancelAction}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confetti celebration */}
      <ConfettiComponent />
    </div>
  )
}
